/**
 * Guardian's "Medicine alert" cards (V8 medicine safety (a), V9 L4195 gMed): a sign someone
 * reported on a new medicine watch (POST /med-watches/:id/signs) opens a Guardian case that
 * carries the reference "med_watch:<id>". Each open one leads the page until it is closed:
 * what was seen on which medicine (the server's own summary), who owns it, and "I'm on it" /
 * "Done, close alert" for the building's staff. Aithora's team reads them and acts on none.
 */
import type { GuardianSignal } from '@aithora/shared';
import { hhmm } from './model';

export interface MedicineAlert {
  id: string;
  head: string;
  sub: string;
  unclaimed: boolean;
  claimed: boolean;
}

export const isMedWatchSignal = (s: GuardianSignal) => (s.refs ?? []).some((r) => r.startsWith('med_watch:'));

export function medicineAlerts(signals: readonly GuardianSignal[], opts: { canAct: boolean; meId: string | null }): MedicineAlert[] {
  return signals
    .filter((s) => isMedWatchSignal(s) && s.stage !== 'closed' && !s.closedAt)
    .sort((a, b) => Date.parse(a.surfacedAt) - Date.parse(b.surfacedAt))
    .map((s) => {
      const who = s.residentName ?? 'A resident';
      const owned = !!s.owner;
      const mine = owned && !!opts.meId && s.owner!.userId === opts.meId;
      const since = s.steps.find((x) => x.step === 'claim')?.at ?? null;
      return {
        id: s.id,
        head: who + (s.room ? ', Rm ' + s.room : '') + ': ' + (s.summary ?? 'a sign on a new medicine'),
        sub: owned
          ? (mine ? 'You own it' : (s.owner!.name ?? 'Someone') + ' owns it') + (since ? ' since ' + hhmm(Date.parse(since)) : '') + '. Work through it, then close it.'
          : 'Reported ' + hhmm(Date.parse(s.surfacedAt)) + '. Nobody has claimed it yet.',
        unclaimed: opts.canAct && !owned,
        claimed: opts.canAct && mine,
      };
    });
}
